import { Link } from "react-router-dom";
import { SITE_URL } from "@/config/site";
import { useLocalePrefix } from "@/hooks/useLocalePrefix";
import { useTranslation } from "react-i18next";
import { ArrowRight, Cpu } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import PageLayout from "@/components/PageLayout";

// Programmatic index of every distinct model, per-locale (/models, /:lng/models).
// i18n-driven (models.* namespace, 15 languages). Prerendered by
// scripts/prerender-seo.js + listed in sitemap.xml.
const ModelsPage = () => {
  const { t } = useTranslation();
  const localePrefix = useLocalePrefix();
  const rawModels = t("models.list", { returnObjects: true });
  const models = Array.isArray(rawModels) ? rawModels : [];

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t("models.seoIndexTitle"),
    itemListElement: models.map((m, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}${localePrefix}/models/${m.slug}`,
      name: m.name,
    })),
  };

  return (
    <PageLayout>
      <SEO
        title={t("models.seoIndexTitle")}
        description={t("models.seoIndexMeta")}
        canonical="/models"
        jsonLd={itemList}
      />
      <section className="section-y-sm px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-primary text-sm font-medium mb-6">
              <Cpu className="w-4 h-4" /> {t("models.badge")}
            </span>
            <h1 className="animate-in-up font-heading text-fluid-h1 font-bold text-white mb-4">
              {t("models.indexH1")}
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              {t("models.indexSubtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {models.map((m) => (
              <Link
                key={m.slug}
                to={`${localePrefix}/models/${m.slug}`}
                className="glass lift rounded-2xl p-6 hover:border-primary/40 group block"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-primary uppercase tracking-wider">
                    {m.developer}
                  </span>
                  <span className="text-[10px] font-semibold text-slate-400 border border-white/10 rounded-full px-2 py-0.5">
                    {m.kind}
                  </span>
                </div>
                <h2 className="font-heading text-xl font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                  {m.name}
                </h2>
                <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                  {m.tagline}
                </p>
                {/* Spec chips — context window + serving providers. Both are
                    cross-locale invariants (CLAUDE.md §5.11.13), so no
                    translation debt; they add the substance a bare card lacks. */}
                <div className="flex flex-wrap items-center gap-1.5 mb-4">
                  {m.context && (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-slate-300">
                      <Cpu className="w-3 h-3 text-primary" />
                      {m.context}
                    </span>
                  )}
                  {Array.isArray(m.providers) &&
                    m.providers.slice(0, 2).map((p) => (
                      <span
                        key={p}
                        className="text-[11px] px-2 py-0.5 rounded-md bg-primary/10 border border-primary/20 text-primary/90"
                      >
                        {p}
                      </span>
                    ))}
                </div>
                <span className="text-primary text-sm flex items-center gap-1 font-medium">
                  {t("models.cardCta")} <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>

          <div className="text-center mt-16">
            <p className="text-slate-400 mb-6">
              {t("models.indexCtaNote")}{" "}
              <Link
                to={`${localePrefix}/pricing`}
                className="text-primary underline underline-offset-2"
              >
                {t("models.seePricing")}
              </Link>{" "}
              ·{" "}
              <Link
                to={`${localePrefix}/compare`}
                className="text-primary underline underline-offset-2"
              >
                {t("models.compareCta")}
              </Link>
              .
            </p>
            <Link to="/register">
              <Button
                size="lg"
                className="btn-glow bg-primary-strong hover:bg-primary-strong/90 text-lg px-8"
              >
                {t("models.ctaBtn")} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ModelsPage;
